/**
 * Hydrating the company profile (Admin ▸ Company, brief §8.0).
 *
 * `hydrateCompany()` runs in `main.tsx` BEFORE the first render: it reads
 * the tenant's saved public fields from `GET /api/public/company`
 * (anonymous, so the login page and the guest pages get them too; the
 * request is preloaded by `index.html`) and merges them over the build
 * profile. It never throws and never waits long: no answer within the
 * timeout, an error or an older API leave the build profile, which is what
 * every build painted before this existed.
 *
 * `loadStaffCompany()` runs next to the module tree once a staff member is
 * signed in (`auth/AuthContext.tsx`): `GET /api/company` adds the fields
 * only staff see (CC mailboxes, ref labels, the key agent).
 *
 * Both answers also carry `locale`: the tenant's portal clock, home currency
 * and local phone codes from its geography (`geo/tenantLocale.ts`, brief
 * §8.4 G4). Without it (an older API) the locale stays Asian Trails'.
 *
 * On a stack that serves many companies they carry `urls: "origin"` too: the
 * guest and app addresses the build does not name follow the page's origin
 * (`applyCompanyHosts`). A one-company stack leaves the key out.
 */
import { applyCompanyHosts, applyCompanyOverrides } from './company';
import { applyLocale } from './geo/tenantLocale';
import { loadOpsGeo } from './geo/opsGeo';

/** The first paint never waits longer than this for the profile. */
export const COMPANY_BOOT_TIMEOUT_MS = 1_500;

type CompanyAnswer = { data?: unknown; locale?: unknown; urls?: unknown };

async function readBody(url: string, timeoutMs: number): Promise<CompanyAnswer | null | undefined> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctrl?.abort(), timeoutMs);
  try {
    // Same request as the <link rel="preload" as="fetch" crossorigin> in
    // index.html (mode cors, same-origin credentials), so the browser hands
    // the preloaded answer over instead of asking twice.
    const res = await fetch(url, { mode: 'cors', credentials: 'same-origin', signal: ctrl?.signal });
    if (!res.ok) return undefined;
    return (await res.json()) as CompanyAnswer | null;
  } finally {
    clearTimeout(timer);
  }
}

export async function hydrateCompany(timeoutMs = COMPANY_BOOT_TIMEOUT_MS): Promise<void> {
  try {
    const body = await readBody('/api/public/company', timeoutMs);
    if (body?.data !== undefined) applyCompanyOverrides(body.data, 'public');
    if (body && typeof body === 'object' && 'urls' in body) applyCompanyHosts(body.urls);
    if (body && typeof body === 'object' && 'locale' in body) applyLocale(body.locale);
  } catch {
    // The build profile stands.
  }
}

export async function loadStaffCompany(timeoutMs = 5_000): Promise<void> {
  // The logs path's destinations and sectors (`geo/opsGeo.ts`, brief §8.4.4
  // G2) load next to the profile, in parallel; a failure keeps Asian Trails'.
  const geo = loadOpsGeo(timeoutMs);
  try {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), timeoutMs);
    try {
      const res = await fetch('/api/company', { credentials: 'include', signal: ctrl?.signal });
      if (!res.ok) return;
      const body = (await res.json()) as CompanyAnswer | null;
      if (body && 'data' in body) applyCompanyOverrides(body.data, 'all');
      if (body && 'urls' in body) applyCompanyHosts(body.urls);
      if (body && 'locale' in body) applyLocale(body.locale);
    } finally {
      clearTimeout(timer);
    }
  } catch {
    // Keep what the bootstrap gave.
  }
  await geo;
}
